/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — WizardLayout component
 */
import cx from 'classnames';
import * as React from 'react';

import { SidebarWidth } from '../../types';

import './WizardLayout.less';

export interface WizardLayoutProps {
  /**
   * Left sidebar content — typically a DS Stepper.
   * Rendered in a fixed-width column with standard vertical padding.
   */
  sidebar: React.ReactNode;

  /**
   * Sidebar column width from the semantic scale.
   * xs: 140px  sm: 180px  md: 220px (default)  lg: 260px  xl: 300px
   */
  sidebarWidth?: SidebarWidth;

  /**
   * Makes the body column scrollable (overflow-y: auto).
   * Set to true when inside a fixed-height container (Modal/Drawer).
   * Default: true
   */
  scrollableBody?: boolean;

  className?: string;

  /**
   * Optional full-width slot rendered above the sidebar/body row.
   * Use for form-level banners (e.g. save errors) that are not scoped to a single step.
   */
  banner?: React.ReactNode;

  /**
   * Extra className applied to the sidebar column.
   * Use DS utility classes (e.g. `"ds-bg-grey1 ds-pl-5"`) to style the sidebar
   * without a consumer-side CSS file.
   */
  sidebarClassName?: string;

  /** Main content area */
  children?: React.ReactNode;
}

const WizardLayout: React.FC<WizardLayoutProps> = ({
  sidebar,
  sidebarWidth = 'md',
  scrollableBody = true,
  className,
  banner,
  sidebarClassName,
  children,
}) => (
  <div
    className={cx(
      'ds-wizard-layout',
      `ds-wizard-layout--sidebar-${sidebarWidth}`,
      scrollableBody && 'ds-wizard-layout--scrollable',
      className
    )}>
    {banner && <div className="ds-wizard-layout__banner">{banner}</div>}
    <div className={cx('ds-wizard-layout__sidebar', sidebarClassName)}>{sidebar}</div>
    <div className="ds-wizard-layout__body">{children}</div>
  </div>
);

WizardLayout.displayName = 'WizardLayout';

export { WizardLayout };
export default WizardLayout;
